import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, LayoutChangeEvent, Pressable, Text, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { SEGMENTED_CONTROL } from '@shared/constants';
import type { ISegmentedControlProps } from '@shared/types';

export function SegmentedControl<T extends string>({ options, value, onChange }: ISegmentedControlProps<T>) {
    const [innerWidth, setInnerWidth] = useState(0);
    const thumbX = useRef(new Animated.Value(0)).current;
    const activeIndex = Math.max(0, options.findIndex(option => option.key === value));
    const segmentWidth = options.length > 0 ? innerWidth / options.length : 0;
    const dragStartX = useRef(0);
    const dragX = useRef(0);
    const isDragging = useRef(false);
    const lastIndex = options.length - 1;

    const selectIndex = (index: number) => {
        const next = options[index];
        if (next && next.key !== value) onChange(next.key);
    };

    const slideTo = useCallback(
        (toValue: number) => {
            Animated.timing(thumbX, { toValue, duration: SEGMENTED_CONTROL.animationDuration, useNativeDriver: true }).start();
        },
        [thumbX],
    );

    const panGesture = Gesture.Pan()
        .runOnJS(true)
        .activeOffsetX([-SEGMENTED_CONTROL.dragActivationOffset, SEGMENTED_CONTROL.dragActivationOffset])
        .onBegin(() => {
            thumbX.stopAnimation();
            dragStartX.current = segmentWidth * activeIndex;
            dragX.current = dragStartX.current;
        })
        .onStart(() => {
            isDragging.current = true;
        })
        .onUpdate(event => {
            dragX.current = Math.min(Math.max(dragStartX.current + event.translationX, 0), segmentWidth * lastIndex);
            thumbX.setValue(dragX.current);
        })
        .onFinalize(() => {
            if (!isDragging.current) return;
            isDragging.current = false;
            const index = Math.round(dragX.current / segmentWidth);
            slideTo(index * segmentWidth);
            selectIndex(index);
        });

    useEffect(() => {
        slideTo(segmentWidth * activeIndex);
    }, [activeIndex, segmentWidth, slideTo]);

    const onLayout = (event: LayoutChangeEvent) => {
        setInnerWidth(event.nativeEvent.layout.width - SEGMENTED_CONTROL.containerPadding * 2);
    };

    return (
        <GestureDetector gesture={panGesture}>
            <View
                onLayout={onLayout}
                className="flex-row rounded-[14px] border border-gray-300 bg-gray-200 p-xs"
                accessibilityRole="tablist"
            >
                {segmentWidth > 0 && (
                    <View pointerEvents="none" className="absolute left-xs top-xs">
                        <Animated.View
                            style={{
                                height: SEGMENTED_CONTROL.segmentHeight,
                                width: segmentWidth,
                                transform: [{ translateX: thumbX }],
                            }}
                        >
                            <View className="h-full rounded-[10px] bg-white shadow-sm" />
                        </Animated.View>
                    </View>
                )}
                {options.map(option => {
                    const isActive = option.key === value;
                    return (
                        <Pressable
                            key={option.key}
                            onPress={() => onChange(option.key)}
                            accessibilityRole="tab"
                            accessibilityState={{ selected: isActive }}
                            className="h-[48px] flex-1 items-center justify-center"
                        >
                            <Text className={`text-md font-semibold ${isActive ? 'text-black' : 'text-gray-500'}`}>
                                {option.label}
                            </Text>
                        </Pressable>
                    );
                })}
            </View>
        </GestureDetector>
    );
}
